import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Language = 'de' | 'en' | 'ar' | 'tr' | 'el';

interface LanguageContextType {
  currentLanguage: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  dir: 'ltr' | 'rtl';
}

interface Translations {
  [key: string]: {
    de: string;
    en: string;
    ar: string;
    tr: string;
    el: string;
  };
}

const translations: Translations = {
  // Header
  'header.search': {
    de: 'Suchen...',
    en: 'Search...',
    ar: 'بحث...',
    tr: 'Ara...',
    el: 'Αναζήτηση...'
  },
  'header.ai_active': {
    de: 'KI Aktiv',
    en: 'AI Active',
    ar: 'الذكاء الاصطناعي نشط',
    tr: 'AI Aktif',
    el: 'AI Ενεργό'
  },
  'header.notifications': {
    de: 'Benachrichtigungen',
    en: 'Notifications',
    ar: 'الإشعارات',
    tr: 'Bildirimler',
    el: 'Ειδοποιήσεις'
  },

  // Navigation
  'nav.dashboard': {
    de: 'Dashboard',
    en: 'Dashboard',
    ar: 'لوحة القيادة',
    tr: 'Gösterge Paneli',
    el: 'Ταμπλό'
  },
  'nav.communication': {
    de: 'Kommunikation',
    en: 'Communication',
    ar: 'التواصل',
    tr: 'İletişim',
    el: 'Επικοινωνία'
  },
  'nav.email': {
    de: 'Email',
    en: 'Email',
    ar: 'البريد الإلكتروني',
    tr: 'E-posta',
    el: 'Email'
  },
  'nav.calendar': {
    de: 'Kalender & Aufgaben',
    en: 'Calendar & Tasks',
    ar: 'التقويم والمهام',
    tr: 'Takvim ve Görevler',
    el: 'Ημερολόγιο & Εργασίες'
  },
  'nav.recruiting': {
    de: 'Recruiting',
    en: 'Recruiting',
    ar: 'التوظيف',
    tr: 'İşe Alım',
    el: 'Πρόσληψη'
  },
  'nav.employees': {
    de: 'Mitarbeiter',
    en: 'Employees',
    ar: 'الموظفون',
    tr: 'Çalışanlar',
    el: 'Υπάλληλοι'
  },
  'nav.documents': {
    de: 'Dokumente',
    en: 'Documents',
    ar: 'المستندات',
    tr: 'Belgeler',
    el: 'Έγγραφα'
  },
  'nav.analytics': {
    de: 'Analytics',
    en: 'Analytics',
    ar: 'التحليلات',
    tr: 'Analizler',
    el: 'Αναλυτικά'
  },
  'nav.settings': {
    de: 'Einstellungen',
    en: 'Settings',
    ar: 'الإعدادات',
    tr: 'Ayarlar',
    el: 'Ρυθμίσεις'
  },

  // Time Tracking
  'time.tracking': {
    de: 'Zeit erfassen',
    en: 'Time Tracking',
    ar: 'تتبع الوقت',
    tr: 'Zaman Takibi',
    el: 'Παρακολούθηση Χρόνου'
  },
  'time.start': {
    de: 'Starten',
    en: 'Start',
    ar: 'بداية',
    tr: 'Başla',
    el: 'Έναρξη'
  },
  'time.stop': {
    de: 'Stoppen',
    en: 'Stop',
    ar: 'توقف',
    tr: 'Durdur',
    el: 'Στοπ'
  },
  'time.today': {
    de: 'Heute',
    en: 'Today',
    ar: 'اليوم',
    tr: 'Bugün',
    el: 'Σήμερα'
  },
  'time.week': {
    de: 'Woche',
    en: 'Week',
    ar: 'أسبوع',
    tr: 'Hafta',
    el: 'Εβδομάδα'
  },
  'time.break': {
    de: 'Pause',
    en: 'Break',
    ar: 'استراحة',
    tr: 'Mola',
    el: 'Διάλειμμα'
  },

  // HR Dashboard
  'hr.overview': {
    de: 'Übersicht',
    en: 'Overview',
    ar: 'نظرة عامة',
    tr: 'Genel Bakış',
    el: 'Επισκόπηση'
  },
  'hr.employees': {
    de: 'Mitarbeiter',
    en: 'Employees',
    ar: 'الموظفون',
    tr: 'Çalışanlar',
    el: 'Υπάλληλοι'
  },
  'hr.absences': {
    de: 'An- und Abwesenheit',
    en: 'Attendance',
    ar: 'الحضور والغياب',
    tr: 'Devam Durumu',
    el: 'Παρουσίες'
  },
  'hr.workflows': {
    de: 'Automatisierungen',
    en: 'Automations',
    ar: 'الأتمتة',
    tr: 'Otomasyonlar',
    el: 'Αυτοματισμοί'
  },
  'hr.timetracking': {
    de: 'Zeiterfassung',
    en: 'Time Tracking',
    ar: 'تتبع الوقت',
    tr: 'Zaman Takibi',
    el: 'Καταγραφή Χρόνου'
  },
  'hr.add_employee': {
    de: 'Mitarbeiter hinzufügen',
    en: 'Add Employee',
    ar: 'إضافة موظف',
    tr: 'Çalışan Ekle',
    el: 'Προσθήκη Υπαλλήλου'
  },

  // Common
  'common.save': {
    de: 'Speichern',
    en: 'Save',
    ar: 'حفظ',
    tr: 'Kaydet',
    el: 'Αποθήκευση'
  },
  'common.cancel': {
    de: 'Abbrechen',
    en: 'Cancel',
    ar: 'إلغاء',
    tr: 'İptal',
    el: 'Ακύρωση'
  },
  'common.delete': {
    de: 'Löschen',
    en: 'Delete',
    ar: 'حذف',
    tr: 'Sil',
    el: 'Διαγραφή'
  },
  'common.edit': {
    de: 'Bearbeiten',
    en: 'Edit',
    ar: 'تعديل',
    tr: 'Düzenle',
    el: 'Επεξεργασία'
  },
  'common.back': {
    de: 'Zurück',
    en: 'Back',
    ar: 'رجوع',
    tr: 'Geri',
    el: 'Πίσω'
  },
  'common.export': {
    de: 'Export',
    en: 'Export',
    ar: 'تصدير',
    tr: 'Dışa Aktar',
    el: 'Εξαγωγή'
  },
  'common.filter': {
    de: 'Filter',
    en: 'Filter',
    ar: 'تصفية',
    tr: 'Filtre',
    el: 'Φίλτρο'
  },
  'common.details': {
    de: 'Details',
    en: 'Details',
    ar: 'التفاصيل',
    tr: 'Detaylar',
    el: 'Λεπτομέρειες'
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem('crm-language');
    return (saved as Language) || 'de';
  });

  useEffect(() => {
    localStorage.setItem('crm-language', currentLanguage);
    // Set document direction for RTL languages (Arabic)
    document.documentElement.dir = currentLanguage === 'ar' ? 'rtl' : 'ltr';
    // Set language attribute
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  const setLanguage = (lang: Language) => {
    setCurrentLanguage(lang);
  };

  const t = (key: string): string => {
    const translation = translations[key];
    if (!translation) {
      console.warn(`Translation key not found: ${key}`);
      return key;
    }
    return translation[currentLanguage] || translation.de;
  };

  const dir = currentLanguage === 'ar' ? 'rtl' : 'ltr';

  return (
    <LanguageContext.Provider value={{ currentLanguage, setLanguage, t, dir }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};